import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { styled } from '@abyss/web/tools/styled';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import React, { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { useConfig } from '../ProviderSearch/context/Analytics';
import { getChipValue, handleGuestPlanNavigation, toSlug } from './Guest.utils';

const FPC_BASE_URL = config('FPC_BASE_URL');

const YearCardStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '342px',
    height: '206px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
});

const YearCard = (
  title: string,
  description: string,
  icon: string,
  buttonText: string,
  coverageType: string
) => {
  const chipValue = getChipValue(coverageType);
  const { navigate } = useRouter();
  const mapKey = useConfig('MAPBOX_KEY');

  const planSelectionData = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA
  );
  const { selectedPlan, lob, stateCode } = planSelectionData
    ? JSON.parse(planSelectionData)
    : {};

  const { zipcode, longitude, latitude } = useGeoLocationStorage() || {};

  const [, getFpcGuestToken] = useFpcGuestToken({});
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';

  return (
    <YearCardStyledCard key={`${title}-${coverageType}`}>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '0px',
            display: 'flex',
            flexDirection: 'column',
            height: '154px',
          },
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '16px',
              alignItems: 'start',
              marginTop: '0px',
              marginBottom: '0px',
              '& > :first-child': {
                margin: '0',
                height: '48px',
                width: '48px',
              },
            },
          }}
        >
          <IconBrand icon={icon} size="48px" />
          <Heading css={{ paddingTop: '16px' }} offset={5}>
            {title}
          </Heading>
          <Text
            css={{
              'abyss-text-root': {
                display: 'inline-block',
                lineHeight: '1.3 !important',
              },
            }}
            size={'$sm'}
          >
            {description}
          </Text>
        </Layout.Stack>
      </Card.Section>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '16px',
            height: '52px',
            width: '342px',
            marginLeft: '0px',
            marginRight: '0px',
          },
        }}
      >
        <Layout.Group>
          <Link
            after={<IconSymbol icon="chevron_right" size="16" />}
            data-testid={`select-${toSlug(title)}-link`} // Use the utility function
            isStandardAnchor
            onClick={() => {
              handleGuestPlanNavigation({
                getFpcGuestToken,
                selectedPlan,
                planYear: title,
                fipscode,
                FPC_BASE_URL: FPC_BASE_URL || '',
                zipcode,
                chipValue,
                coverageType,
                navigate,
                lob,
                stateCode,
                longitude,
                latitude,
                mapKey,
                isDual: true,
              });
            }}
            size={'sm'}
          >
            {buttonText}
          </Link>
        </Layout.Group>
      </Card.Section>
    </YearCardStyledCard>
  );
};

export const GuestPlanYearSelection = () => {
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';

  const lagoonUIMessage = useLagoon('ui-messaging')();
  const currentYear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_CURRENT_YEAR,
  })?.message;
  const futureYear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_FUTURE_YEAR,
  })?.message;
  const yearSelectionDescription = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_DESCRIPTION,
  })?.message;
  const yearSelectionButtonText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_BUTTON_TEXT,
  })?.message;

  const chooseYearText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_YEAR,
  })?.message;
  const chooseYearHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_YEAR_HEADING,
  })?.message;
  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message;
  const signInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.url;

  const { setChooseType } = useGuestStore(
    useShallow((state) => ({
      setChooseType: (state as GuestState).setChooseType,
    }))
  );
  useEffect(() => {
    if (chooseYearText) {
      setChooseType(chooseYearText);
    }
  }, [chooseYearText, setChooseType]);

  return (
    <React.Fragment>
      <Heading
        css={{ marginTop: '32px' }}
        data-testid="year-selection"
        offset={3}
        textAlign="center"
      >
        {chooseYearHeadingText}
      </Heading>

      <Flex
        alignItems="baseline"
        css={{ marginTop: '8px' }}
        direction="row"
        justify="center"
      >
        <Text>{signInMessageText}</Text>
        <Link
          data-testid={`sign-in-link`}
          href={signInUrl}
          openNewWindow={false}
          style={{ marginLeft: '4px' }}
        >
          {signInText}
        </Link>
      </Flex>

      <Flex
        alignItems="baseline"
        direction="row"
        justify="center"
        style={{ gap: '24px', marginTop: '32px' }}
      >
        {YearCard(
          currentYear,
          `${yearSelectionDescription} ${currentYear}`,
          'calendar',
          `${yearSelectionButtonText} ${currentYear}`,
          coverageType
        )}
        {YearCard(
          futureYear,
          `${yearSelectionDescription} ${futureYear}`,
          'calendar',
          `${yearSelectionButtonText} ${futureYear}`,
          coverageType
        )}
      </Flex>
    </React.Fragment>
  );
};
